import { pxToRem } from '@/shared/styles/themes'
import { Button as BaseButton } from '@mui/material'
import { alpha, css, styled } from '@mui/material/styles'

const sizeMap = {
  small: {
    height: 32,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: 32,
    fontSize: 12,
    iconMargin: 4,
  },
  medium: {
    height: 36,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: 36,
    fontSize: 14,
    iconMargin: 4,
  },
  large: {
    height: 48,
    padding: `${pxToRem(12)} ${pxToRem(16)}`,
    borderRadius: 48,
    fontSize: 16,
    iconMargin: 10,
  },
} as const

export const StyledButton = styled(BaseButton, {
  shouldForwardProp: (prop) => prop !== '$round' && prop !== '$icon' && prop !== '$arrow',
})<{ $round?: boolean; $icon?: boolean; $arrow?: boolean }>`
  min-width: 0;
  text-transform: none !important;
  min-height: ${props => pxToRem(sizeMap[props.size || 'medium'].height)};
  padding: ${props => sizeMap[props.size || 'medium'].padding};
  font-size: ${props => pxToRem(sizeMap[props.size || 'medium'].fontSize)};
  border-radius: ${props => pxToRem(sizeMap[props.size || 'medium'].borderRadius)};
  line-height: 1.2;
  white-space: normal;
  font-weight: 400;

  .MuiButton-startIcon {
    margin-right: ${props => pxToRem(sizeMap[props.size || 'medium'].iconMargin)} !important;
    &>*:nth-of-type(1) {
      font-size: ${props => pxToRem(sizeMap[props.size || 'medium'].fontSize)};
    }
  }

  .MuiButton-endIcon {
    margin-left: ${props => pxToRem(sizeMap[props.size || 'medium'].iconMargin)} !important;
    &>*:nth-of-type(1) {
      font-size: ${props => pxToRem(sizeMap[props.size || 'medium'].fontSize)};
    }
  }

  ${props => props.theme.breakpoints.up('md')} {
    ${props => props.$icon && css`
      width: ${pxToRem(sizeMap[props.size || 'medium'].height)};
    `}
  }
  
  ${props => props.variant === 'text' && css`
    color: ${props.theme.palette.text.primary};
    font-weight: 400;
    white-space: nowrap;
    height: auto !important;
    line-height: unset;
    padding: ${sizeMap[props.size || 'medium'].padding};
    background-color: transparent;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      border-radius: ${props.$round ? pxToRem(sizeMap[props.size || 'medium'].height) : pxToRem(sizeMap[props.size || 'medium'].borderRadius)};
      background-color: ${props.theme.palette.custom.textButtonShadow};
      opacity: 0;
      transform: scaleX(0.7) scaleY(0.7) perspective(1px);
    }

    &:before {
      transition: transform 0.25s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.25s ease;
    }

    &:after {
      transition: transform 0.2s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease;
    }

    &:hover {
      background-color: transparent;

      &:after,
      &:before {
        opacity: 1;
        background-color: ${props.theme.palette.custom.textButtonShadow};
        transform: scaleX(1.015) scaleY(1.035) perspective(1px);
      }
    }

    &:active {
      &:after,
      &:before {
        transform: scaleX(0.95) scaleY(0.95) perspective(1px);
      }
    }
  `}

  ${props => props.variant === 'contained' && css`
    box-shadow: none;
    transition: background-color 0.2s ease, opacity 0.2s ease;
    font-weight: 500;

    &.MuiButton-colorPrimary {
      background-color: ${props.theme.palette.secondary.main};
      color: ${props.theme.palette.primary.main};

      &:hover {
        background-color: ${alpha(props.theme.palette.secondary.main, 0.85)};
      }

      &:active {
        opacity: 0.7;
      }
    }

    &.MuiButton-colorSecondary {
      background-color: ${props.theme.palette.primary.main};
      color: ${props.theme.palette.secondary.main};

      &:hover {
        background-color: ${alpha(props.theme.palette.primary.main, 0.85)};
      }

      &:active {
        opacity: 0.7;
      }
    }
  `}

  ${props => props.variant === 'outlined' && css`
    &.MuiButton-colorPrimary:active {
      background-color: ${alpha(props.theme.palette.primary.main, 0.2)};
    }

    &.MuiButton-colorSecondary:active {
      background-color: ${alpha(props.theme.palette.secondary.main, 0.2)};
    }
  `}

  ${props => props.$round && css`
    min-width: unset;
    border-radius: ${pxToRem(sizeMap[props.size || 'medium'].height)};

    .MuiButton-startIcon {
      margin: 0;
    }
  `}

  ${props => props.$arrow && css`
    position: relative;
    overflow: hidden;

    & .MuiButton-label,
    & > span:not(.MuiButton-startIcon):not(.MuiButton-endIcon) {
      display: inline-flex;
      align-items: center;
      transition: transform 0.3s ease;
    }

    &::after {
      content: '→';
      position: absolute;
      right: ${pxToRem(16)};
      opacity: 0;
      transform: translateX(${pxToRem(-8)});
      transition: opacity 0.3s ease, transform 0.3s ease;
      font-size: ${pxToRem(sizeMap[props.size || 'medium'].fontSize)};
    }

    &:hover {
      & .MuiButton-label,
      & > span:not(.MuiButton-startIcon):not(.MuiButton-endIcon) {
        transform: translateX(${pxToRem(-8)});
      }

      &::after {
        opacity: 1;
        transform: translateX(0);
      }
    }
  `}
`